import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Constantes } from 'app/config/constants';
import { environment } from 'environments/environment';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { SwalAlertHelper } from '../helpers/swal-alert-helper';
import { Usuario } from '../../models/sistema/usuario';
import * as data from '../../../assets/data/newreport/empresas.json';
import { Compania } from 'app/models/sistema/compania';
import { ConvertDataHelper } from '../helpers/convert-data-helper';
import { StatusCodeResponseHelper } from '../helpers/status-code-response-helper';

/** Fondo del login guardado en el navegador (se conserva al cerrar sesión). */
export interface FondoLoginGuardado {
    companiaId: string;
    version: number;
    imagen: string;
}

@Injectable({
    providedIn: 'root',
})
export class AccesoService {
    private readonly claveFondoLogin = 'fondoLogin';
    /** Copia en memoria del último fondo sincronizado: el login lo usa aunque el navegador no haya podido guardarlo. */
    private fondoLoginMemoria: FondoLoginGuardado = null;
    urlAccesos = environment.urlEsErpAccesos;
    apiAccesos = Constantes.api.accesos;
    usuario: Usuario;
    compania: Compania;
    token: string;
    inventario: boolean;
    aperturaCaja = false;
    sucursalCompaniaId = '';
    nombreSucursalCompania = '';
    direccionSucursalCompania = '';
    listadoAccesos: any;
    listaSucursalCompania: any;
    empresas: any = (data as any).default;

    constructor(
        private _httpClient: HttpClient,
        private router: Router,
        private _fuseNavigationService: FuseNavigationService,
        private _swalAlertHelper: SwalAlertHelper,
        private _convertDataHelper: ConvertDataHelper,
        private _statusCodeResponseHelper: StatusCodeResponseHelper
    ) {
        this.asignarHeaders();
    }

    asignarHeaders(): void {
        this.cargarStorage();
        this.cargarStorageSucursal();
    }

    obtenerAccesos(): Observable<any> {
        const url = this.urlAccesos + this.apiAccesos.Acceso.GetMenu;
        return this._httpClient.get(url)
            .pipe(
                map((resp: any) => {
                    this.listadoAccesos = this.quitarPrefijoEss(resp.resResult);
                    localStorage.setItem('listadoAccesos', JSON.stringify(this.listadoAccesos));
                    return true;
                }
                )
            );
    }

    login(usuario: Usuario, recordar: boolean = false): Observable<any> {
        const url = this.urlAccesos + this.apiAccesos.Login.Login;
        return this._httpClient.post(url, usuario)
            .pipe(
                map((resp: any) => {
                    const resultado = this.procesarMensajeLogin(resp);

                    if (recordar) {
                        localStorage.setItem('email', usuario.correoElectronico);
                        localStorage.setItem('contrasenia', usuario.password);
                    } else {
                        localStorage.removeItem('email');
                        localStorage.removeItem('contrasenia');
                    }
                    return resultado;
                }),
                catchError(
                    error => {
                        console.log(error)
                        this._swalAlertHelper.swalEventoWarning({ mensajeSwal: error.error.msg });
                        this.router.navigate(['/login']);
                        return throwError(error);
                    }
                )
            );
    }

    procesarMensajeLogin(resp: any) {
        if (resp.sesion && resp.sesion.estado=="Sesion activa") {
            this._swalAlertHelper.swalEventoInfo({ mensajeSwal:  resp.sesion.mensaje }, {showConfirmButton:true,mensaje: 'Ir a dashboard...'}).
            then((result) => {
                if (result.isConfirmed) {
                    this.router.navigate(['/sistema/dashboard']);
                }
            });
            return false;
        }
        if (!resp.licencia) {
            this._swalAlertHelper.swalEventoUrgente({ mensajeSwal: 'No cuenta con una licencia activa, por favor comuniquese con nosotros.' });
            return false;
        }
        if (resp.licencia.licencia === 'vencida' || resp.licencia.licencia === 'sin licencia') {
            this._swalAlertHelper.swalEventoUrgente({ mensajeSwal: resp.licencia.mensaje }, {showConfirmButton:false}).
            then((result) => {
                if (result.isConfirmed) {
                    // this.router.navigate(['/sistema/dashboard']);
                }
            });
            return false;
        }

        if (resp.licencia.licencia === 'por vencerse') {

            this._swalAlertHelper.swalEventoUrgente({ mensajeSwal: resp.licencia.mensaje }, {showConfirmButton:false, mensaje: 'Ir a pagos...'}).
            then((result) => {
                if (result.isConfirmed) {
                    // this.router.navigate(['/sistema/cuenta']);
                }
            });
        }

        this.guardarStorage(resp);
        // Fondo del login de la compañía: se descarga solo si cambió y queda en el navegador para el próximo ingreso
        if (resp.fondoLogin && resp.fondoLogin.companiaId) {
            this.sincronizarFondoLogin(resp.fondoLogin.companiaId, resp.fondoLogin.version);
        }
        if (resp.sucursales.length > 0) {
            this.cambiarSucusal(resp.sucursales[0]._id, resp.sucursales[0]);
        }
        return true;
    }

    guardarStorage(usuarioCompania: any): void {
        localStorage.setItem('token', usuarioCompania.token);
        localStorage.setItem('usuario', JSON.stringify(usuarioCompania.usuario));
        localStorage.setItem('listadoAccesos', JSON.stringify(usuarioCompania.accesos));
        localStorage.setItem('listaSucursalCompania', JSON.stringify(usuarioCompania.sucursales));
        localStorage.setItem('compania', JSON.stringify(usuarioCompania.compania));
    }

    // ---------- Fondo del login ----------

    /** Fondo guardado en este navegador, o null. Lee también la clave antigua 'fondo' (solo URL). */
    obtenerFondoLoginGuardado(): FondoLoginGuardado {
        try {
            const guardado = JSON.parse(localStorage.getItem(this.claveFondoLogin) || 'null');
            if (guardado && this.esImagenFondoValida(guardado.imagen)) {
                return guardado as FondoLoginGuardado;
            }
        } catch (e) { }
        if (this.fondoLoginMemoria != null) {
            return this.fondoLoginMemoria;
        }
        try {
            const anterior = localStorage.getItem('fondo');
            if (this.esImagenFondoValida(anterior)) {
                return { companiaId: '', version: 0, imagen: anterior };
            }
            // Valor antiguo inválido (texto, URL rota…): se descarta para no dejar el login en blanco
            localStorage.removeItem('fondo');
        } catch (e) { }
        return null;
    }

    /** Imagen utilizable como fondo: data URL de imagen, URL http(s) o ruta de assets. */
    esImagenFondoValida(valor: any): boolean {
        if (typeof valor !== 'string') {
            return false;
        }
        const texto = valor.trim();
        return /^data:image\/(png|jpe?g|webp|gif);base64,/i.test(texto) || /^https?:\/\/\S+$/i.test(texto) || /^\/?assets\//.test(texto);
    }

    private guardarFondoLogin(fondo: FondoLoginGuardado): void {
        this.fondoLoginMemoria = fondo && fondo.imagen ? fondo : null;
        try {
            if (fondo && fondo.imagen) {
                localStorage.setItem(this.claveFondoLogin, JSON.stringify(fondo));
            } else {
                localStorage.removeItem(this.claveFondoLogin);
            }
            localStorage.removeItem('fondo');
        } catch (e) {
            // Sin espacio en el navegador: el login usa el fondo estándar
            console.warn('No se pudo guardar el fondo del login en el navegador (se usará solo en esta pestaña)', e);
        }
    }

    /**
     * Trae del servidor el fondo de la compañía solo si cambió respecto al guardado en el navegador.
     * Devuelve la imagen vigente ('' = sin fondo propio) o undefined si no se pudo consultar.
     */
    sincronizarFondoLogin(companiaId: string, versionServidor?: number): Promise<string> {
        const guardado = this.obtenerFondoLoginGuardado();
        const mismaCompania = guardado != null && guardado.companiaId === String(companiaId);
        if (mismaCompania && versionServidor != null && guardado.version === versionServidor && versionServidor > 0) {
            return Promise.resolve(guardado.imagen);
        }
        const url = `${this.urlAccesos}${this.apiAccesos.Compania.FondoLogin}/${companiaId}`;
        const params: any = mismaCompania ? { version: String(guardado.version) } : {};
        return this._httpClient.get<any>(url, { params }).toPromise()
            .then(resp => {
                if (resp && resp.sinCambios && mismaCompania) {
                    return guardado.imagen;
                }
                const imagen = resp && resp.imagenFondo ? resp.imagenFondo : '';
                this.guardarFondoLogin(imagen ? { companiaId: String(companiaId), version: Number(resp.version || 0), imagen } : null);
                return imagen;
            })
            .catch(error => {
                console.warn('No se pudo consultar el fondo del login', error);
                return undefined;
            });
    }

    cargarStorage(): void {
        if (localStorage.getItem('token')) {
            this.token = localStorage.getItem('token');
            this.usuario = JSON.parse(localStorage.getItem('usuario'));
            this.listadoAccesos = this.quitarPrefijoEss(JSON.parse(localStorage.getItem('listadoAccesos')));
            this.listaSucursalCompania = JSON.parse(localStorage.getItem('listaSucursalCompania'));
            this.compania = JSON.parse(localStorage.getItem('compania'));
            this.inventario = localStorage.getItem('inventario').toLowerCase() === 'true';
            this.aperturaCaja = localStorage.getItem('aperturaCaja') === 'true';
            this.sucursalCompaniaId = localStorage.getItem('sucursalCompaniaId');
        } else {
            this.limpiarStorage();
        }
    }

    // Las URL del menú guardadas como /ess/... se usan sin el prefijo /ess
    quitarPrefijoEss(items: any): any {
        if (!Array.isArray(items)) return items;
        return items.map(item => {
            const nuevo = { ...item };
            if (typeof nuevo.url === 'string' && /^\/?ess\//.test(nuevo.url)) nuevo.url = nuevo.url.replace(/^\/?ess\//, '/');
            if (Array.isArray(nuevo.children)) nuevo.children = this.quitarPrefijoEss(nuevo.children);
            return nuevo;
        });
    }

    estaLogueado(): boolean {
        return (this.token.length > 5) ? true : false;
    }

    revisarAccesosRuta(ruta: any): boolean {
        const resultado = this.evaluarAccesoRuta(this.listadoAccesos, ruta);
        return resultado;
    }

    evaluarAccesoRuta(listadoAccesos: any, ruta: any): any {
        let resultado = false;
        for (let index = 0; index < listadoAccesos.length; index++) {
            const element = listadoAccesos[index];
            if (element.children && element.children.length > 0) {
                resultado = this.evaluarAccesoRuta(element.children, ruta);
                if (resultado) {
                    return resultado;
                }
            } else {
                if (element.url.indexOf(ruta) >= 0) {
                    resultado = true;
                    return resultado;
                }
            }
        }
        return resultado;
    }

    limpiarStorage(): void{
        this.token = '';
        this.usuario = new Usuario();
        this.compania = null;
        this.listadoAccesos = [];
        this.listaSucursalCompania = [];
        this.inventario = false;
        this.aperturaCaja = false;
        localStorage.removeItem('aperturaCaja');
        localStorage.removeItem('token');
        localStorage.removeItem('sucursalCompaniaId');
        localStorage.removeItem('nombreSucursalCompania');
        localStorage.removeItem('usuario');
        localStorage.removeItem('listadoAccesos');
        localStorage.removeItem('listaSucursalCompania');
        localStorage.removeItem('compania');
    }

    logout(): Observable<any> {
        console.log("resp")

        const url = this.urlAccesos + this.apiAccesos.Login.Logout;
        return this._httpClient.post(url,this.usuario)
            .pipe(
                map((resp: any) => {
                    console.log(resp)
                    if (resp.mensaje =="ok") {

                        this.limpiarStorage();
                        this._fuseNavigationService.unregister('main');
                        this.router.navigate(['/login']);
                    }
 
                    return true;
                }),
                catchError(
                    error => {
                        this.router.navigate(['/login']);
                        return throwError(error);
                    }

                )
            );

    }

    renuevaToken(): Observable<any> {
        const url = this.urlAccesos + this.apiAccesos.Login.RenuevaToken;
        return this._httpClient.post(url, this.usuario)
            .pipe(
                map((resp: any) => {
                    this.token = resp.token;
                    localStorage.setItem('token', this.token);
                    return true;
                }),
                catchError(
                    error => {
                        this.router.navigate(['/login']);
                        return throwError(error);
                    }
                )
            );
    }

    cambiarSucusal(sucursalCompaniaId, sucursalConectado): any {
        this.guardarStorageSucursal(sucursalCompaniaId, sucursalConectado);
        this.asignarHeaders();
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';
        this.router.navigate(['/sistema/dashboard']);
    }

    cargarStorageSucursal(): any {
        if (localStorage.getItem('sucursalCompaniaId')) {
            this.sucursalCompaniaId = localStorage.getItem('sucursalCompaniaId');
            this.nombreSucursalCompania = localStorage.getItem('nombreSucursalCompania');
        } else {
            this.sucursalCompaniaId = '';
            this.nombreSucursalCompania = '';
        }
    }

    guardarStorageSucursal(sucursalCompaniaId, sucursalConectado): any {
        localStorage.setItem('sucursalCompaniaId', sucursalCompaniaId);
        localStorage.setItem('nombreSucursalCompania', sucursalConectado.nombreSucursal);
        this.sucursalCompaniaId = sucursalCompaniaId;
        this.nombreSucursalCompania = sucursalConectado.nombreSucursal;
        this.direccionSucursalCompania = sucursalConectado.direccion;
        this.inventario = sucursalConectado.inventario;
        localStorage.setItem('inventario', String(sucursalConectado.inventario));
        this.aperturaCaja = sucursalConectado.aperturaCaja === true;
        localStorage.setItem('aperturaCaja', String(this.aperturaCaja));
    }

    obtenerMenuCompania(): Observable<any> {
        const url = this.urlAccesos + this.apiAccesos.Acceso.GetMenuAdministrativoCompania + '/' + this.usuario.companiaId + '/Ordenado';
        return this._httpClient.get(url)
            .pipe(
                map((resp: any) => {
                    return resp;
                }),
                catchError((error) => {
                    return null;
                })
            );
    }

    obtenerMenuRol(rolId: any): Observable<any> {
        const url = this.urlAccesos + this.apiAccesos.Acceso.GetMenuRol + '/' + rolId;
        return this._httpClient.get(url)
            .pipe(
                map((resp: any) => {
                    return resp;
                }),
                catchError((error) => {
                    return null;
                })
            );
    }

    obtenerPeriodoComprobante(criterio: any): Observable<any> {
        const params = this._convertDataHelper.obtenerParametrosGet(criterio);
        const url = this.urlAccesos + this.apiAccesos.ControlComprobante.GetPeriodo;
        return this._httpClient.get(url,  {
                params: params
            })
            .pipe(
                map((resp: any) => {
                    return resp;
                }),
                catchError(
                    error => this._statusCodeResponseHelper.statusResponse(error)
                )
            );
    }

    actualizarEntidad(listadoAcceso: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const url = `${this.urlAccesos}${this.apiAccesos.Acceso.Actualizar}`;
            return this._httpClient
                .post(url, listadoAcceso)
                .pipe(
                    map((resp: any) => {
                        return resp;
                    }),
                    catchError((error) => {
                        return null;
                    })
                )
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    getExtention(): any {
        const rucCompania = JSON.parse(atob(this.token.split('.')[1])).usuario.rucCompania;
        let extension = ".xls"
        if (this.empresas.filter(x => x.ruc == rucCompania) && this.empresas.filter(x => x.ruc == rucCompania).length > 0)
            extension = ".xlsx";
        return extension;
    }
}
